import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { EASE } from "./Motion";
import { YouTubeLayer } from "./YouTubeLayer";

const R = "/assets/reels/";

const Toggle = ({ playing, onClick }) => (
  <button
    data-testid="brand-reel-toggle"
    aria-label={playing ? "Pause reel" : "Play reel"}
    onClick={onClick}
    className="flex h-12 w-12 items-center justify-center rounded-full border border-white/30 bg-[#0a0b0d]/50 text-[#f5f3ef] backdrop-blur-md transition-colors duration-300 hover:border-[#c8a97e] hover:bg-[#c8a97e] hover:text-[#0a0b0d]"
  >
    {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
  </button>
);

export const BrandReel = ({ reel, label }) => {
  const wrap = useRef(null);
  const video = useRef(null);
  const inView = useInView(wrap, { amount: 0.25 });
  const [paused, setPaused] = useState(false);
  const [ytFailed, setYtFailed] = useState(false);
  const onYtError = useCallback(() => setYtFailed(true), []);
  const { scrollYProgress } = useScroll({ target: wrap, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.18, 1, 1.08]);

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (inView && !paused) v.play().catch(() => {});
    else v.pause();
  }, [inView, paused]);

  const frame = reel.vertical ? "mx-auto aspect-[9/16] w-full max-w-[460px]" : "aspect-[4/5] w-full sm:aspect-video";

  return (
    <section data-testid="brand-reel" className="mx-auto mt-24 max-w-[1600px] px-6 sm:px-10 lg:px-14">
      <div className="mb-8 flex items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#c8a97e]">Campaign Reel</p>
          <p className="mt-3 font-display text-4xl italic sm:text-5xl">{reel.title}</p>
        </div>
        <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-[#b4b2ad] sm:block">{label}</span>
      </div>
      <motion.div ref={wrap} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }}>
        <motion.div
          className={`relative overflow-hidden bg-[#121418] ${frame}`}
          variants={{ hidden: { clipPath: "inset(18% 10% 18% 10%)" }, show: { clipPath: "inset(0% 0% 0% 0%)" } }}
          transition={{ duration: 1.5, ease: EASE }}
        >
          {reel.youtube ? (
            <motion.div style={{ scale }} className="absolute inset-0">
              <img src={reel.poster} alt={reel.title} className="absolute inset-0 h-full w-full object-cover" />
              {!ytFailed && <YouTubeLayer id={reel.youtube} vertical={!!reel.vertical} playing={inView && !paused} onError={onYtError} testid="brand-reel-youtube" />}
            </motion.div>
          ) : (
          <motion.video
            ref={video}
            style={{ scale }}
            data-testid="brand-reel-video"
            className="h-full w-full object-cover"
            src={`${R}${reel.name}.mp4`}
            poster={`${R}${reel.name}.jpg`}
            muted
            loop
            playsInline
            preload="metadata"
          />
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0b0d]/60 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
            {ytFailed ? (
              <a data-testid="brand-reel-youtube-link" href={`https://www.youtube.com/watch?v=${reel.youtube}`} target="_blank" rel="noreferrer" className="link-u pb-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#f5f3ef]">Watch on YouTube</a>
            ) : (
              <>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#f5f3ef]">{paused ? "Paused" : "Now playing"}</span>
                <Toggle playing={!paused} onClick={() => setPaused((p) => !p)} />
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
